import React from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import { useTranslation } from 'hooks/useTranslation';
import { Buttons, Loaders } from 'shared';

import useUser from 'features/user/hooks/useUser';
import { Paper } from 'features/app/components/Paper';
import BaseBalanceDropdown from 'features/app/components/BaseBalanceDropdown';
import { BalanceCurrency } from 'features/app/ts/BalanceCurrency';
import { TopUpBalanceModal } from 'features/finance/components/TopUpBalanceModal';

import { useStyles } from './styles';
import i18n from 'locales/i18n';
import { loadStripe } from '@stripe/stripe-js';
import { StripePublicKey } from 'features/finance/constants/stripe-public-key';
import { Elements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(StripePublicKey);

type Props = {
  partnerBalance?: BalanceCurrency[];
};

export const AvailableUserBalance: React.FC<Props> = ({ partnerBalance }) => {
  const { t } = useTranslation('finance');
  const direction = i18n.dir();

  const classes = useStyles({ direction });

  const { isLoading: isUserDataLoading, error, userRolesTitle, userFullName } = useUser();

  const [displayedBalanceId, setDisplayedBalanceId] = React.useState<string | null>(null);
  const [isTopUpBalanceModelOpened, setIsTopUpBalanceModelOpened] = React.useState(false);

  const displayedBalance = React.useMemo(
    () => partnerBalance?.find(item => item.id === displayedBalanceId) ?? partnerBalance?.[0],
    [displayedBalanceId, partnerBalance]
  );

  const toggleTopUpBalanceModal = () => setIsTopUpBalanceModelOpened(!isTopUpBalanceModelOpened);

  const balances = React.useMemo(
    () => (partnerBalance ? partnerBalance.filter(balance => balance.id !== displayedBalance?.id) : []),
    [displayedBalance, partnerBalance]
  );

  const createCurrencyChangeHandler = React.useCallback((item: BalanceCurrency) => {
    setDisplayedBalanceId(item.id);
  }, []);

  return (
    <Paper className={classes.container}>
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" className={classes.title}>
                {t('account')}
              </Typography>
              <Typography className={classes.userFullName}>{userFullName}</Typography>
              <Typography variant="subtitle1" className={classes.userRolesTitle}>
                {userRolesTitle}
              </Typography>
            </Box>

            {isUserDataLoading ? <Loaders.Loader /> : null}

            <Box className={classes.availableBalanceWrapper}>
              <Typography variant="h5" className={classes.title} align="right">
                {t('availableBalance')}
              </Typography>

              <BaseBalanceDropdown
                options={balances}
                displayedBalance={displayedBalance}
                onChange={createCurrencyChangeHandler}
                bordered={false}
                customContainerClassName={classes.balanceDropdown}
              />
            </Box>
          </Box>

          <Buttons.WithLoading
            isLoading={isUserDataLoading}
            variant="text"
            className={classes.topUpButton}
            labelClass={classes.topUpButtonLabel}
            onClick={toggleTopUpBalanceModal}
          >
            + {t('topUpBalance')}
          </Buttons.WithLoading>

          <Typography className={classes.topUpBalanceByTransfer}>
            {t('topUpBalanceByBankTransfer')} <br /> {t('sendingUsRequest')}{' '}
            <span className={classes.email}>care@rewardsby.com</span>
          </Typography>

          <Elements stripe={stripePromise}>
            <TopUpBalanceModal open={isTopUpBalanceModelOpened} onClose={() => setIsTopUpBalanceModelOpened(false)} />
          </Elements>
        </>
      )}
    </Paper>
  );
};
